import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import NavbarMenu from "../../../../components/NavbarMenu";
import { getCarrito } from "../hooks/carritoService";
import { getProductosPublicos } from "../../../../services/productosPublicos";
import { resolverImagenUrl } from "../../../../utils/imagen";
import ImagenLightbox from "../../../menuDigital/ImagenLightbox";

const CATEGORIAS_EXCLUIDAS = [
  "ADICIONAL FRUTA",
  "ADICIONAL TOPPINGS",
  "ADICIONAL COMIDA",
];

const normalizar = (v) =>
  String(v ?? "")
    .toUpperCase()
    .trim();

export default function PedidoLista() {
  const [productos, setProductos] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [categoriasAbiertas, setCategoriasAbiertas] = useState([]);
  const [cantidadCarrito, setCantidadCarrito] = useState(0);

  const [imagenZoom, setImagenZoom] = useState(null);

  const navigate = useNavigate();

  useEffect(() => {
    let alive = true;

    async function cargarProductos() {
      try {
        const data = await getProductosPublicos();
        if (!alive) return;

        const productosBase = (data || []).filter(
          (p) =>
            !CATEGORIAS_EXCLUIDAS.includes(
              normalizar(p.categoria?.nombre)
            )
        );

        setProductos(productosBase);

        const categoriasMap = new Map();

        productosBase.forEach((p) => {
          if (p.categoria?.id) {
            categoriasMap.set(p.categoria.id, p.categoria);
          }
        });

        const categoriasOrdenadas = Array.from(
          categoriasMap.values()
        ).sort((a, b) => {
          if (a.orden === 0) return 1;
          if (b.orden === 0) return -1;
          return (a.orden ?? 0) - (b.orden ?? 0);
        });

        setCategorias(categoriasOrdenadas);
      } catch (e) {
        console.error("Error cargando productos", e);
      }
    }

    cargarProductos();
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    const carrito = getCarrito();
    const total = carrito.items.reduce(
      (sum, p) => sum + p.cantidad,
      0
    );
    setCantidadCarrito(total);
  }, []);

  const toggleCategoria = (catNombre) => {
    setCategoriasAbiertas((prev) =>
      prev.includes(catNombre)
        ? prev.filter((c) => c !== catNombre)
        : [...prev, catNombre]
    );
  };

  return (
    <section className="min-h-screen bg-black text-white">
      <NavbarMenu />

      <div className="pt-28 px-6 pb-28 max-w-5xl mx-auto space-y-6">
        <h1 className="text-3xl font-bold text-orange-400 text-center">
          Elige tus productos
        </h1>

        <div className="space-y-4">
          {categorias.map((cat) => (
            <div
              key={cat.id}
              className="bg-[#0f0f0f] rounded-xl border border-white/10 overflow-hidden"
            >
              {/* HEADER */}
              <button
                onClick={() => toggleCategoria(cat.nombre)}
                className="w-full px-4 py-3 flex justify-between items-center
                           text-lg font-semibold text-orange-400"
              >
                {cat.nombre}
                <span
                  className={`transition-transform ${
                    categoriasAbiertas.includes(cat.nombre)
                      ? "rotate-180"
                      : ""
                  }`}
                >
                  ⌄
                </span>
              </button>

              {/* PRODUCTOS */}
              {categoriasAbiertas.includes(cat.nombre) && (
                <div className="px-4 pb-4 space-y-3">
                  {productos
                    .filter(
                      (p) => p.categoria?.nombre === cat.nombre
                    )
                    .map((p) => {
                      const noDisponible =
                        p.bloqueado === true ||
                        p.categoria?.activo === false;

                      const img =
                        p.imagenFinal || p.imagen || p.categoria?.imagen;

                      return (
                        <div
                          key={p.id}
                          className={`flex justify-between items-center gap-3
                                     py-3 border-b border-white/10 transition
                                     ${
                                       noDisponible
                                         ? "opacity-50 grayscale"
                                         : ""
                                     }`}
                        >
                          {/* IZQUIERDA: IMAGEN + INFO */}
                          <div className="flex items-center gap-3">
                            {img && (
                              <img
                                src={resolverImagenUrl(img)}
                                alt={p.nombre}
                                className="w-14 h-14 object-cover rounded-lg
                                           border border-white/10
                                           cursor-zoom-in
                                           hover:scale-105 transition"
                                onClick={() => setImagenZoom(img)}
                                onError={(e) => {
                                  e.currentTarget.style.display = "none";
                                }}
                              />
                            )}

                            <div className="flex flex-col gap-0.5">
                              <span className="font-medium text-sm flex items-center gap-2">
                                {p.nombre}

                                {noDisponible && (
                                  <span className="text-xs bg-red-600 text-white px-2 py-0.5 rounded-full">
                                    No disponible
                                  </span>
                                )}
                              </span>

                              {p.ingredientes && (
                                <span className="text-xs text-gray-400 leading-snug">
                                  <span className="font-semibold text-orange-300">
                                    Ingredientes:
                                  </span>{" "}
                                  {p.ingredientes}
                                </span>
                              )}

                              <span className="text-orange-400 text-sm">
                                $
                                {Number(p.precio).toLocaleString(
                                  "es-CO"
                                )}
                              </span>
                            </div>
                          </div>

                          {/* BOTÓN */}
                          <button
                            disabled={noDisponible}
                            onClick={() =>
                              navigate(
                                `/pedido/producto/${p.id}`,
                                { state: { producto: p } }
                              )
                            }
                            className={`px-3 py-1.5 rounded-md text-sm font-semibold transition
                              ${
                                noDisponible
                                  ? "bg-gray-600 text-gray-300 cursor-not-allowed"
                                  : "bg-orange-500 text-black hover:bg-orange-400"
                              }`}
                          >
                            Elegir
                          </button>
                        </div>
                      );
                    })}

                  {productos.filter(
                    (p) => p.categoria?.nombre === cat.nombre
                  ).length === 0 && (
                    <p className="text-gray-400 text-sm">
                      No hay productos disponibles
                    </p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        <button
          onClick={() => navigate("/pedido/carrito")}
          className="fixed bottom-6 right-6 bg-green-500 text-black
                     px-6 py-3 rounded-full font-bold shadow-lg
                     flex items-center gap-2"
        >
          Carrito

          {cantidadCarrito > 0 && (
            <span
              className="absolute -top-2 -right-2 bg-red-600
                         text-white text-xs font-bold
                         w-6 h-6 rounded-full
                         flex items-center justify-center"
            >
              {cantidadCarrito}
            </span>
          )}
        </button>
      </div>

      {/* 🔍 IMAGEN EN GRANDE */}
      <ImagenLightbox
        src={imagenZoom}
        alt="Imagen del producto"
        onClose={() => setImagenZoom(null)}
      />
    </section>
  );
}
